import React, { useEffect, useState, useMemo } from 'react';
import { contractRequestAPI, appointmentAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { PipelineTiles } from '@/components/contracting/PipelineTiles';
import { RecentRequestsStrip } from '@/components/contracting/RecentRequestsStrip';

/**
 * AgencyContractingHealth — second Command Center band on the Agency
 * dashboard. Mirrors KR ContractingHealth but scoped to the logged-in
 * agency's downline. Uses the SAME shared primitives (`PipelineTiles`,
 * `RecentRequestsStrip`) and the SAME canonical taxonomy so counts
 * reconcile with the Agency Command Center profile and /requests.
 *
 * Data sources (agency-scoped by backend for non-KR callers):
 *   • GET /contract-requests   — full agency-scoped requests dataset.
 *   • GET /appointments        — full agency-scoped appointments dataset.
 *
 * No new endpoints. No raw status literals. No duplicated pipeline logic.
 */

const RECENT_LIMIT = 5;

const metaFormatter = (r) =>
  [r?.agent_name, r?.contract_type_name].filter(Boolean).join(' · ');

const sortByRecency = (a, b) => {
  const at = new Date(a?.updated_at || a?.created_at || 0).getTime();
  const bt = new Date(b?.updated_at || b?.created_at || 0).getTime();
  return bt - at;
};

export const AgencyContractingHealth = () => {
  const [requests, setRequests] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [reqRes, apptRes] = await Promise.all([
          contractRequestAPI.list(),
          appointmentAPI.list(),
        ]);
        if (cancelled) return;
        setRequests(Array.isArray(reqRes.data) ? reqRes.data : []);
        setAppointments(Array.isArray(apptRes.data) ? apptRes.data : []);
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load agency contracting health data:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const recentRequests = useMemo(
    () => [...requests].sort(sortByRecency).slice(0, RECENT_LIMIT),
    [requests],
  );

  const isEmpty = !loading && requests.length === 0 && appointments.length === 0;

  return (
    <section className="mb-8" data-testid="agency-contracting-health">
      <div className="mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Contracting Health
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Your agency&apos;s contract requests, approvals, and active writing numbers.
        </p>
      </div>

      <Card className="animate-slideIn">
        <CardContent className="p-5">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400" data-testid="agency-contracting-health-loading">
              Loading contracting health…
            </div>
          ) : isEmpty ? (
            <div className="py-8 text-center" data-testid="agency-contracting-health-empty">
              <p className="text-sm font-medium text-slate-800">No contracting activity yet</p>
              <p className="text-xs text-slate-500 mt-1">
                Your first contract requests will appear here once your team submits them.
              </p>
            </div>
          ) : (
            <>
              <PipelineTiles
                requests={requests}
                appointments={appointments}
                testIdPrefix="agency-pipeline"
              />
              {recentRequests.length === 0 ? (
                <div
                  className="mt-5 pt-4 border-t text-center py-4"
                  style={{ borderColor: 'var(--kr-border)' }}
                  data-testid="agency-contracting-health-no-recent"
                >
                  <p className="text-sm text-slate-500">No recent contract activity</p>
                </div>
              ) : (
                <RecentRequestsStrip
                  requests={recentRequests}
                  maxItems={RECENT_LIMIT}
                  testIdPrefix="agency-recent-request"
                  metaFormatter={metaFormatter}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default AgencyContractingHealth;
